/* ⚠️ GENERATED from app/services/tickTone.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   services/tickTone.ts — the tick colours, measured against what they sit on. Pure.
   The app reads it through src/utils/tickColors (whose header tells the story); gen-twins copies it
   to the web client, whose themed bubbles and lists take the same colours.
   ═══════════════════════════════════════════════════════════════════════════ */

/** the read (seen) tick — the one colour, app-wide */
export const READ_BLUE = '#34B7F1';
function parseRGB(color) {
  if (!color || typeof color !== 'string') return null;
  const c = color.trim();

  // Gradient encoded as a JSON array of color stops → average the stops for a representative tone.
  if (c.startsWith('[')) {
    try {
      const arr = JSON.parse(c);
      if (Array.isArray(arr) && arr.length) {
        const rgbs = arr.map(s => parseRGB(String(s))).filter(Boolean);
        if (!rgbs.length) return null;
        const n = rgbs.length;
        return {
          r: Math.round(rgbs.reduce((s, x) => s + x.r, 0) / n),
          g: Math.round(rgbs.reduce((s, x) => s + x.g, 0) / n),
          b: Math.round(rgbs.reduce((s, x) => s + x.b, 0) / n)
        };
      }
    } catch {
      return null;
    }
    return null;
  }
  if (c.startsWith('#')) {
    let hex = c.slice(1);
    if (hex.length === 3) hex = hex.split('').map(ch => ch + ch).join('');
    if (hex.length >= 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      if (![r, g, b].some(v => Number.isNaN(v))) return {
        r,
        g,
        b
      };
    }
    return null;
  }
  const m = c.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const parts = m[1].split(',').map(s => parseFloat(s.trim()));
    if (parts.length >= 3 && parts.slice(0, 3).every(v => !Number.isNaN(v))) {
      return {
        r: parts[0],
        g: parts[1],
        b: parts[2]
      };
    }
  }
  return null;
}

/** Perceived brightness 0 (black) .. 1 (white). */
function luminance(rgb) {
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
}

/** WCAG relative luminance 0..1 */
function relLum(c) {
  const f = v => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(c.r) + 0.7152 * f(c.g) + 0.0722 * f(c.b);
}
/** WCAG contrast ratio 1..21 */
export function contrastRatio(a, b) {
  const x = relLum(a);
  const y = relLum(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
const over = (v, a, bg) => ({
  r: v * a + bg.r * (1 - a),
  g: v * a + bg.g * (1 - a),
  b: v * a + bg.b * (1 - a)
});
const apart = (a, b) => Math.hypot(a.r - b.r, a.g - b.g, a.b - b.b);
const hexRGB = h => parseRGB(h);

/** the read tick's family: the brand blue first, then lighter, then deeper */
export const READ_FAMILY = [READ_BLUE, '#5AC8FA', '#5CE1FF', '#00D4FF', '#1D9BF0', '#0A84FF', '#8EDCFF', '#A6EEFF', '#C8F5FF', '#0B63B6', '#0050D0', '#0040C0', '#0047AB', '#064A8A', '#003A75', '#002B5C'];
/** the delivered tick: light or dark, translucent */
const MUTED_LIGHT = [{
  v: 255,
  a: 0.78
}, {
  v: 255,
  a: 0.6
}];
const MUTED_DARK = [{
  v: 0,
  a: 0.5
}, {
  v: 0,
  a: 0.6
}];
/** the bars it has to clear */
export const READ_MIN_CONTRAST = 3;
export const MUTED_MIN_CONTRAST = 2.4;
export const READ_MUTED_APART = 90;

/** every colour a bubble shows: one for a solid, each stop for a gradient */
export function bubbleStops(color) {
  const c = String(color || '').trim();
  if (c.startsWith('[')) {
    try {
      const arr = JSON.parse(c);
      if (Array.isArray(arr)) return arr.map(x => parseRGB(String(x))).filter(Boolean);
    } catch {/* below */}
    return [];
  }
  const one = parseRGB(c);
  return one ? [one] : [];
}

/** where the ticks sit: the bottom-right corner. A gradient bubble runs top-left → bottom-right, so
 *  the ticks sit on its last stretch — measured at 70 % and at its end, not against colours they
 *  never touch (a cyan → deep blue gradient has no one blue that reads on both ends). */
export function tickArea(color) {
  const stops = bubbleStops(color);
  if (stops.length < 2) return stops;
  const at = t => {
    const x = t * (stops.length - 1);
    const i = Math.min(stops.length - 2, Math.floor(x));
    const f = x - i;
    const a = stops[i];
    const b = stops[i + 1];
    return {
      r: a.r + (b.r - a.r) * f,
      g: a.g + (b.g - a.g) * f,
      b: a.b + (b.b - a.b) * f
    };
  };
  return [at(0.7), at(1)];
}
/** the delivered and read ticks for these bubble colours, chosen together. Pure. */
export function pickTicks(stops) {
  const avg = stops.length ? stops.reduce((s, x) => ({
    r: s.r + x.r / stops.length,
    g: s.g + x.g / stops.length,
    b: s.b + x.b / stops.length
  }), {
    r: 0,
    g: 0,
    b: 0
  }) : {
    r: 141,
    g: 151,
    b: 165
  };
  const lightSideFirst = luminance(avg) <= 0.6;
  const mutedOrder = lightSideFirst ? [...MUTED_LIGHT, ...MUTED_DARK] : [...MUTED_DARK, ...MUTED_LIGHT];
  let best = null;
  for (const m of mutedOrder) {
    for (const hex of READ_FAMILY) {
      const rc = hexRGB(hex);
      let cr = Infinity;
      let cm = Infinity;
      let dm = Infinity;
      for (const bg of stops) {
        const mc = over(m.v, m.a, bg);
        cr = Math.min(cr, contrastRatio(rc, bg));
        cm = Math.min(cm, contrastRatio(mc, bg));
        dm = Math.min(dm, apart(rc, mc));
      }
      const score = Math.min(cr / READ_MIN_CONTRAST, cm / MUTED_MIN_CONTRAST, dm / READ_MUTED_APART);
      const pick = {
        muted: `rgba(${m.v},${m.v},${m.v},${m.a})`,
        read: hex,
        score,
        ok: score >= 1
      };
      if (pick.ok) return pick; // the first that clears every bar, in preference order
      if (!best || score > best.score) best = pick;
    }
  }
  return best;
}

/** the read tick on a plain surface (the chat list, a white card, Message info) */
const readMemo = new Map();
export function readTickOn(surface) {
  const key = String(surface || '');
  const hit = readMemo.get(key);
  if (hit) return hit;
  const stops = bubbleStops(surface);
  const out = stops.length ? pickTicks(stops).read : READ_BLUE;
  if (readMemo.size > 200) readMemo.clear();
  readMemo.set(key, out);
  return out;
}
const memo = new Map();

/**
 * @param bubbleColor the effective outgoing bubble color (hex / rgb / gradient-JSON).
 * @param opts.onMedia ticks sit on a photo / video / dark gradient → force light ticks.
 */
export function getAdaptiveTickColors(bubbleColor, opts) {
  if (opts?.onMedia) {
    // On a photo we can't know the pixels under the ticks → bright, high-contrast set.
    return {
      muted: 'rgba(255,255,255,0.95)',
      read: READ_BLUE
    };
  }
  const key = String(bubbleColor || '');
  const hit = memo.get(key);
  if (hit) return hit;
  const stops = tickArea(bubbleColor);
  // Unknown color → safe neutral that reads on most surfaces.
  const out = stops.length ? (({
    muted,
    read
  }) => ({
    muted,
    read
  }))(pickTicks(stops)) : {
    muted: '#8D97A5',
    read: READ_BLUE
  };
  if (memo.size > 200) memo.clear();
  memo.set(key, out);
  return out;
}
